import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import ThemeModeProvider from "../theme/ThemeModeProvider";
import { useThemeMode } from "../hooks/ThemeMode";
import useCall from "../hooks/Call";
import MyTextField from "../components/ui/Inputs/TextField/MyTextField";
import { MyPanel, MyPanelBody, MyPanelHeader } from "../components/ui/Layout/MyPanel";

const FALLBACK_REDIRECT = "/-/";
const LOGIN_HERO_IMAGE = "/public/images/auth/login-hero.webp";
const LOGIN_LOGO_IMAGE = "/public/images/auth/login-logo.svg";
const INVALID_CREDENTIALS_MESSAGE = "Usuario o clave incorrectos";
const LOGIN_UNAVAILABLE_MESSAGE = "No se pudo iniciar sesión. Intenta nuevamente.";

function resolveLoginErrorMessage(error) {
  // Objetivo: no renderizar detalles tecnicos recibidos desde red, proxy o servidor.
  return [401, 403].includes(Number(error?.status))
    ? INVALID_CREDENTIALS_MESSAGE
    : LOGIN_UNAVAILABLE_MESSAGE;
}

function normalizeMessages(messages) {
  if (!Array.isArray(messages)) return [];
  return messages
    .map((message) => ({
      type: String(message?.Type || message?.type || "error").toLowerCase(),
      text: String(message?.Text || message?.text || "").trim(),
    }))
    .filter((message) => message.text !== "");
}

function parseInitialProps() {
  const node = document.getElementById("react-auth-props");
  if (!node) {
    return {
      title: "MaujaGroup - Cuenta :: Iniciar",
      messages: [],
      rememberedUsername: "",
      remember: false,
      currentYear: new Date().getFullYear(),
    };
  }
  try {
    const parsed = JSON.parse(node.textContent || "{}");
    return {
      title: parsed?.Title || "MaujaGroup - Cuenta :: Iniciar",
      messages: normalizeMessages(parsed?.Messages),
      rememberedUsername: parsed?.RememberedUsername || "",
      remember: Boolean(parsed?.Remember),
      currentYear: Number(parsed?.CurrentYear) || new Date().getFullYear(),
    };
  } catch (error) {
    return {
      title: "MaujaGroup - Cuenta :: Iniciar",
      messages: [],
      rememberedUsername: "",
      remember: false,
      currentYear: new Date().getFullYear(),
    };
  }
}

function AuthLoginContent({ title, messages, rememberedUsername, remember, currentYear }) {
  const { mode, toggleMode } = useThemeMode();
  const loginCall = useCall("/-/v1/auth/login");

  const [form, setForm] = useState({
    username: rememberedUsername || "",
    password: "",
    remember: Boolean(remember),
  });
  const [showPassword, setShowPassword] = useState(false);
  const [runtimeError, setRuntimeError] = useState("");

  const alerts = useMemo(() => {
    const list = [...messages];
    if (runtimeError) {
      list.unshift({ type: "error", text: runtimeError });
    }
    return list;
  }, [messages, runtimeError]);

  const handleInput = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setRuntimeError("");
    try {
      const res = await loginCall.$post({
        username: form.username.trim(),
        password: form.password,
        remember: form.remember,
      });
      const redirectTo = res?.data?.redirect_to || FALLBACK_REDIRECT;
      window.location.assign(redirectTo);
    } catch (error) {
      setRuntimeError(resolveLoginErrorMessage(error));
    }
  };

  const submitting = loginCall.isLoading;

  return (
    <Grid container sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Grid
        item
        xs={false}
        md={7.5}
        sx={{
          display: { xs: "none", md: "block" },
          position: "relative",
          backgroundImage: `linear-gradient(0deg, rgba(7,42,69,0.74), rgba(7,42,69,0.74)), url(${LOGIN_HERO_IMAGE})`,
          backgroundPosition: "left center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 6,
            textAlign: "center",
            color: "#fff",
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              El centro de mando de tus ventas multicanal.
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Configuración de tiendas, gestión de pedidos, publicaciones y seguimiento logístico, con trazabilidad
              de punta a punta.
            </Typography>
          </Stack>
        </Box>
      </Grid>

      <Grid
        item
        xs={12}
        md={4.5}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 4 },
          py: { xs: 3, md: 4 },
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 560,
            transform: { md: "translateY(-2.5vh)" },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2.2 }}>
            <Box
              role="img"
              aria-label="Logo MiTraza"
              sx={{
                width: { xs: 220, md: 440 },
                height: { xs: 94, md: 188 },
                bgcolor: "secondary.main",
                WebkitMaskImage: `url(${LOGIN_LOGO_IMAGE})`,
                WebkitMaskRepeat: "no-repeat",
                WebkitMaskSize: "contain",
                WebkitMaskPosition: "center",
                maskImage: `url(${LOGIN_LOGO_IMAGE})`,
                maskRepeat: "no-repeat",
                maskSize: "contain",
                maskPosition: "center",
              }}
            />
          </Box>

          <MyPanel
            sx={{
              borderColor: "divider",
              boxShadow: "none",
            }}
          >
            <MyPanelHeader
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <LockOutlinedIcon fontSize="small" />
                <Typography variant="subtitle1" component="h1" sx={{ fontWeight: 700 }}>
                  Iniciar sesión
                </Typography>
              </Stack>
              <IconButton
                size="small"
                aria-label="Cambiar tema"
                onClick={toggleMode}
                sx={{ color: "text.secondary" }}
              >
                {mode === "dark" ? (
                  <LightModeOutlinedIcon fontSize="small" />
                ) : (
                  <DarkModeOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </MyPanelHeader>

            <MyPanelBody sx={{ p: 3 }}>
              <Stack spacing={2.2}>
                <Typography variant="body2" color="text.secondary">
                  {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Usa tus credenciales corporativas para continuar.
                </Typography>

                {alerts.map((message, index) => (
                  <Alert key={`${message.type}-${index}`} severity={message.type === "success" ? "success" : "error"}>
                    {message.text}
                  </Alert>
                ))}

                <Box component="form" onSubmit={onSubmit} noValidate>
                  <Stack spacing={1.5}>
                    <MyTextField
                      label="Usuario"
                      name="username"
                      value={form.username}
                      onChange={handleInput}
                      required
                      autoComplete="username"
                      disabled={submitting}
                      clearable={false}
                    />
                    <MyTextField
                      label="Clave"
                      name="password"
                      value={form.password}
                      onChange={handleInput}
                      type={showPassword ? "text" : "password"}
                      required
                      autoComplete="current-password"
                      disabled={submitting}
                      clearable={false}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              tabIndex={-1}
                              aria-label={showPassword ? "Ocultar clave" : "Mostrar clave"}
                              onClick={() => setShowPassword((prev) => !prev)}
                            >
                              {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(form.remember)}
                          onChange={(event) =>
                            setForm((prev) => ({ ...prev, remember: event.target.checked }))
                          }
                          name="remember"
                          color="secondary"
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Recordarme en este equipo
                        </Typography>
                      }
                      sx={{ my: 0.2 }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      disabled={submitting}
                      sx={{ mt: 1, minHeight: 42 }}
                    >
                      {submitting ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={16} color="inherit" />
                          <span>Ingresando...</span>
                        </Stack>
                      ) : (
                        "Ingresar"
                      )}
                    </Button>
                  </Stack>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                  Copyright © Mauja Group {currentYear}
                </Typography>
              </Stack>
            </MyPanelBody>
          </MyPanel>
        </Box>
      </Grid>
    </Grid>
  );
}

function AuthLoginApp(props) {
  return (
    <ThemeModeProvider>
      <AuthLoginContent {...props} />
    </ThemeModeProvider>
  );
}

export function mountReactAuthLogin() {
  const mountNode = document.getElementById("react-auth-root");
  if (!mountNode || mountNode.dataset.reactMounted === "1") return;
  const props = parseInitialProps();
  const root = createRoot(mountNode);
  root.render(<AuthLoginApp {...props} />);
  mountNode.dataset.reactMounted = "1";
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountReactAuthLogin, { once: true });
} else {
  mountReactAuthLogin();
}

window.mountReactAuthLogin = mountReactAuthLogin;
